/**
 * WaveformCanvas — v10.3.2
 *
 * Canvas eficiente para dibujar UNA onda fisiológica en modo sweep.
 *
 * BUG FIX P0 (v10.3.2 — reportado en prod): el canvas capturaba
 * `getBoundingClientRect()` UNA SOLA VEZ al montar. Si el layout aún no
 * había calculado dimensiones (grid/flex asincrónico, transiciones de
 * mount), `rect.width === 0` → todo el loop dibujaba en un canvas de
 * 0×0 y las ondas quedaban invisibles aunque `patient_current_state`
 * tuviera datos correctos. Ahora:
 *   - ResizeObserver reinicia el canvas cuando el parent cambia de
 *     dimensiones (incluida la primera vez que > 0).
 *   - El draw loop no se inicia hasta W > 0.
 *   - Log de diagnóstico opcional via `debug` prop.
 */
import { useEffect, useRef } from 'react';

export const WaveformCanvas = ({
  sampler,
  pxPerSecond = 90,
  height = 120,
  color = '#22c55e',
  yScale = 1,
  label,
  sublabel,
  numericValue,
  numericUnit,
  numericColor,
  testId,
  grid = true,
  bg = '#050b18',
  debug = false,
}) => {
  const canvasRef = useRef(null);
  const samplerRef = useRef(sampler);
  samplerRef.current = sampler;
  // Refs for the active draw loop so ResizeObserver puede pararlo y
  // relanzarlo con nuevas dimensiones sin cerrar el componente.
  const rafRef = useRef(null);
  const dimsRef = useRef({ W: 0, H: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');

    const setupCanvas = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      const W = Math.floor(rect.width);
      const H = Math.floor(rect.height);
      if (W <= 0 || H <= 0) {
        if (debug) console.warn('[WaveformCanvas] skip setup — 0 dims', { W, H });
        return false;
      }
      canvas.width = Math.floor(W * dpr);
      canvas.height = Math.floor(H * dpr);
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
      dimsRef.current = { W, H };

      // Fondo + grilla
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, W, H);
      if (grid) drawGrid(ctx, W, H);

      if (debug) console.info('[WaveformCanvas] setup', { W, H, dpr, color });
      return true;
    };

    const drawGrid = (ctxg, W, H) => {
      ctxg.strokeStyle = 'rgba(148,163,184,0.08)';
      ctxg.lineWidth = 1;
      for (let gx = 0; gx <= W; gx += 20) {
        ctxg.beginPath(); ctxg.moveTo(gx, 0); ctxg.lineTo(gx, H); ctxg.stroke();
      }
      for (let gy = 0; gy <= H; gy += 20) {
        ctxg.beginPath(); ctxg.moveTo(0, gy); ctxg.lineTo(W, gy); ctxg.stroke();
      }
      ctxg.strokeStyle = 'rgba(148,163,184,0.16)';
      for (let gx = 0; gx <= W; gx += 100) {
        ctxg.beginPath(); ctxg.moveTo(gx, 0); ctxg.lineTo(gx, H); ctxg.stroke();
      }
      for (let gy = 0; gy <= H; gy += 100) {
        ctxg.beginPath(); ctxg.moveTo(0, gy); ctxg.lineTo(W, gy); ctxg.stroke();
      }
    };

    let x = 0;
    let prevY = 0;
    let last = performance.now();
    let samplesGenerated = 0;
    let debugTick = 0;

    const eraseColumn = (px) => {
      const { W, H } = dimsRef.current;
      if (W <= 0 || H <= 0) return;
      // v10.3.5: limpiamos un ancho generoso (24px) para asegurar que
      // no queden restos del sweep anterior. Sin shadowBlur el pixel
      // coverage por stroke es predecible; con líneas de 2.6px, 24px
      // ahead es suficiente y no daña el rendimiento.
      ctx.fillStyle = bg;
      ctx.fillRect(px, 0, 24, H);
      if (grid) {
        ctx.strokeStyle = 'rgba(148,163,184,0.08)';
        ctx.lineWidth = 1;
        for (let gy = 0; gy <= H; gy += 20) {
          ctx.beginPath(); ctx.moveTo(px, gy); ctx.lineTo(px + 24, gy); ctx.stroke();
        }
      }
    };

    const draw = (now) => {
      const { W, H } = dimsRef.current;
      if (W <= 0 || H <= 0) {
        rafRef.current = requestAnimationFrame(draw);
        return;
      }
      const midY = H / 2;
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      const px = Math.max(1, Math.round(pxPerSecond * dt));

      // v10.3.6: set stroke properties para las ondas UNA VEZ fuera del
      // for-loop se pisaba con la config de la grilla (color gris,
      // opacity 0.08) que setea eraseColumn(). Ahora reasignamos el
      // color de la onda en CADA iteración justo antes del stroke.
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      for (let i = 0; i < px; i++) {
        const nextX = (x + 1) % W;
        eraseColumn(nextX);
        const sampleT = now + (i / pxPerSecond) * 1000;
        const raw = samplerRef.current(sampleT);
        // Clamp usando min/max correctos (no valores invertidos).
        const y = Math.min(H - 2, Math.max(2, midY - raw * yScale));
        // Restaurar propiedades del stroke de la onda ANTES del stroke
        // (eraseColumn() dejó strokeStyle=grid-gray y lineWidth=1).
        ctx.lineWidth = 2.6;
        ctx.strokeStyle = color;
        // v10.3.7: detectar wrap-around del sweep. Si nextX < x significa
        // que acabamos de cruzar el borde derecho — NO conectar el punto
        // anterior con el nuevo, porque sino se dibuja una línea horizontal
        // atravesando todo el canvas (bug reportado en screenshot).
        const wrapped = nextX < x;
        if (!wrapped) {
          ctx.beginPath();
          ctx.moveTo(x, prevY || midY);
          ctx.lineTo(nextX, y);
          ctx.stroke();
        }
        prevY = y;
        x = nextX;
        samplesGenerated += 1;
      }

      if (debug) {
        debugTick += 1;
        if (debugTick % 60 === 0) {
          const { W: cW, H: cH } = dimsRef.current;
          console.info('[WAVEFORM_RENDER_LOOP]', {
            label, samples_generated: samplesGenerated, W: cW, H: cH,
            fps: Math.round(1000 / (dt * 1000 || 16)),
          });
        }
      }

      rafRef.current = requestAnimationFrame(draw);
    };

    // Intento inicial de setup. Si falla (0×0), ResizeObserver lo reintentará.
    const ok = setupCanvas();
    prevY = dimsRef.current.H / 2 || 0;

    // Reinicia el canvas cuando cambian las dimensiones (montaje diferido,
    // resize del navegador, sidebar toggle, etc.)
    let ro = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => {
        const oldW = dimsRef.current.W;
        const oldH = dimsRef.current.H;
        const changed = setupCanvas();
        if (changed && (dimsRef.current.W !== oldW || dimsRef.current.H !== oldH)) {
          // Reset del sweep para no dejar restos del canvas previo
          x = 0;
          prevY = dimsRef.current.H / 2;
        }
      });
      ro.observe(canvas);
    }

    // Fallback para navegadores sin ResizeObserver: polling breve durante
    // los primeros 2s por si el layout tardó en calcularse.
    let attempts = 0;
    const retryId = ok ? null : setInterval(() => {
      attempts += 1;
      if (setupCanvas() || attempts > 20) {
        prevY = dimsRef.current.H / 2;
        clearInterval(retryId);
      }
    }, 100);

    rafRef.current = requestAnimationFrame(draw);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (ro) ro.disconnect();
      if (retryId) clearInterval(retryId);
    };
  }, [pxPerSecond, height, color, yScale, bg, grid, debug, label]);

  return (
    <div className="relative w-full" style={{ height }}>
      <canvas
        ref={canvasRef}
        className="w-full h-full block"
        style={{ background: bg, width: '100%', height: '100%' }}
        data-testid={testId}
      />
      {(label || numericValue != null) && (
        <div className="absolute top-1.5 left-2 flex items-center gap-2 pointer-events-none">
          {label && (
            <span className="text-[10px] uppercase tracking-wide text-slate-400 font-medium">
              {label}
            </span>
          )}
          {numericValue != null && (
            <span
              className="font-vital text-sm"
              style={{ color: numericColor || color }}
            >
              {numericValue}
              {numericUnit && (
                <span className="text-[10px] text-slate-500 ml-0.5">{numericUnit}</span>
              )}
            </span>
          )}
          {sublabel && (
            <span className="text-[10px] text-slate-500 uppercase tracking-wide">
              {sublabel}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default WaveformCanvas;
